import { useTranslation } from 'react-i18next';
import { ChevronRight, Loader2 } from 'lucide-react';
import { Link } from 'react-router-dom';

interface BreadcrumbSegment {
  path: string;
  title: string;
  isLoading?: boolean;
}

interface BreadcrumbProps {
  items: BreadcrumbSegment[];
}

/**
 * Breadcrumb component for displaying navigation hierarchy
 *
 * Features:
 * - Links to intermediate paths (last item is non-clickable)
 * - Truncates gracefully on small screens
 * - Shows loading state for hydrating entity names
 * - Separates items with chevron icons
 *
 * @example
 * <Breadcrumb items={[
 *   { path: '/vehicles', title: 'Vehicles' },
 *   { path: '/vehicles/abc', title: 'VW Golf (XX-123-Y)' }
 * ]} />
 */
export default function Breadcrumb({ items }: BreadcrumbProps) {
  const { t } = useTranslation('common');

  if (!items || items.length === 0) {
    return null;
  }

  return (
    <nav
      aria-label={t('accessibility.breadcrumb')}
      className="flex items-center gap-1 text-sm min-w-0 overflow-x-auto"
    >
      {items.map((item, index) => {
        const isLast = index === items.length - 1;

        return (
          <div key={item.path} className="flex items-center gap-1 min-w-0 shrink-0">
            {isLast ? (
              // Last item: non-clickable, highlighted
              <span
                className="truncate font-medium text-foreground flex items-center gap-1"
                aria-current="page"
              >
                {item.title}
                {item.isLoading && (
                  <Loader2 className="h-3 w-3 animate-spin text-muted-foreground" />
                )}
              </span>
            ) : (
              // Intermediate items: clickable links
              <Link
                to={item.path}
                className="truncate text-muted-foreground hover:text-foreground transition-colors flex items-center gap-1"
              >
                {item.title}
                {item.isLoading && <Loader2 className="h-3 w-3 animate-spin" />}
              </Link>
            )}

            {!isLast && (
              <ChevronRight className="h-4 w-4 shrink-0 text-muted-foreground" aria-hidden="true" />
            )}
          </div>
        );
      })}
    </nav>
  );
}
